import {Component, ElementRef, EventEmitter, OnInit, ViewChild} from '@angular/core';
import {AppService} from '@app/services/app.service';
import {Store} from '@ngxs/store';
import {LogService} from '@app/services/log.service';
import {WRTCProviderService} from '@app/modules/wrtc/services/providers/wrtc-provider';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {LogLevel, LogResource} from '@app/modules/shared/data/constants';
import {LocalAudioTrack} from 'twilio-video/tsdef/LocalAudioTrack';

@Component({
  selector: 'app-audio-video-device-configuration-dialog',
  templateUrl: './media-device-configuration.component.html'
})
export class MediaDeviceConfigurationComponent implements OnInit {
  @ViewChild('videoMediaWrapper') videoMediaWrapper: ElementRef<HTMLElement>;

  videoDevicesMetadata: any [];
  audioDevicesMetadata: any [];
  currentSelectedDeviceInfo: any = {
    audio: {id: -1, level: -1},
    video: {id: -1}
  };
  isStreamsLoadRequestFinished = false;
  cameraDevice;
  microphoneDevice;

  publishedVideoDeviceId = localStorage.getItem('video_device_id');
  publishedAudioDeviceId = localStorage.getItem('audio_device_id');
  isCameraAccessProvided = false;
  isMicrophoneAccessProvided = false;
  isIos = false;
  disableButtons = false;

  form = new FormGroup({
    camera: new FormControl('', Validators.required)
  });
  private voiceMeterInterval: NodeJS.Timeout;

  constructor(public dialog: NgbActiveModal, private wrtcProviderService: WRTCProviderService) {
  }

  ngOnInit(): void {
    if (this.isIos) {
      this.wrtcProviderService.detachLocalParticipantVideo();
    }
    this.wrtcProviderService.getVideoAndAudioDevices().subscribe(audioVideoDevicesMetadata => {
      this.videoDevicesMetadata = audioVideoDevicesMetadata.video;
      this.audioDevicesMetadata = audioVideoDevicesMetadata.audio;

      const videoDeviceIndex = this.getCurrentVideoDeviceIndex();
      const audioDeviceIndex = this.getCurrentAudioDeviceIndex();

      if (videoDeviceIndex !== -1) {
        this.onSelectCameraDevice(null, videoDeviceIndex);
        this.cameraDevice = this.videoDevicesMetadata[videoDeviceIndex].id;
      }

      if (audioDeviceIndex !== -1) {
        this.onSelectMicrophoneDevice(audioDeviceIndex);
        this.microphoneDevice = this.audioDevicesMetadata[audioDeviceIndex].id;
      }
      // free up unused video streams for another applications
      for (const item of this.videoDevicesMetadata) {
        if (item.active) {
          this.wrtcProviderService.detachAndStopVideoTrack(item.id, false);
        }
      }

      // free up unused audio streams for another applications

      for (const item of this.audioDevicesMetadata) {
        if (item.active) {
          this.wrtcProviderService.stopAudioTrack(item.id);
        }
      }

      for (const item of this.videoDevicesMetadata) {
        if (item.id && item.id !== '' && item.active) {
          this.isCameraAccessProvided = true;
          break;
        }
      }

      for (const item of this.audioDevicesMetadata) {
        if (item.id && item.id !== '' && item.active) {
          this.isMicrophoneAccessProvided = true;
          break;
        }
      }
      this.isStreamsLoadRequestFinished = true;
    });

  }

  getCurrentAudioDeviceIndex(): number {
    let audioDeviceIndex = -1;
    if (this.publishedAudioDeviceId) {
      for (let i = 0; i < this.audioDevicesMetadata.length; i++) {
        if (this.audioDevicesMetadata[i].id === this.publishedAudioDeviceId && this.audioDevicesMetadata[i].active) {
          audioDeviceIndex = i;
          break;
        }
      }
    }

    if (audioDeviceIndex === -1) {
      for (let i = 0; i < this.audioDevicesMetadata.length; i++) {
        if (this.audioDevicesMetadata[i].active) {
          audioDeviceIndex = i;
          break;
        }
      }
    }

    return audioDeviceIndex;
  }

  getCurrentVideoDeviceIndex() {
    let videoDeviceIndex = -1;
    if (this.publishedVideoDeviceId) {
      for (let i = 0; i < this.videoDevicesMetadata.length; i++) {
        if (this.videoDevicesMetadata[i].id === this.publishedVideoDeviceId && this.videoDevicesMetadata[i].active) {
          videoDeviceIndex = i;
          break;
        }
      }
    }

    if (videoDeviceIndex === -1) {
      for (let i = 0; i < this.videoDevicesMetadata.length; i++) {
        if (this.videoDevicesMetadata[i].active) {
          videoDeviceIndex = i;
          break;
        }
      }
    }
    return videoDeviceIndex;

  }

  onCancel() {
    if (this.currentSelectedDeviceInfo.video.id !== -1 && !this.isIos) {
      this.wrtcProviderService.detachAndStopVideoTrack(this.currentSelectedDeviceInfo.video.id, false);
    }

    if (this.currentSelectedDeviceInfo.audio.id !== -1 && !this.isIos) {
      this.wrtcProviderService.stopAudioTrack(this.currentSelectedDeviceInfo.audio.id);
    }

    this.disableButtons = true;
    if (this.isIos) {
      this.wrtcProviderService.republishLocalParticipantVideoTrack();
      this.wrtcProviderService.republishLocalParticipantAudioTrack();
    } else {
      this.wrtcProviderService.reattachLocalParticipantVideoTrack();
    }
    this.dialog.dismiss();

  }

  onSubmit() {
    this.disableButtons = true;
    if (this.publishedVideoDeviceId && this.currentSelectedDeviceInfo.video.id !== -1
      && this.publishedVideoDeviceId === this.currentSelectedDeviceInfo.video.id) {
      const currentDeviceId = this.currentSelectedDeviceInfo.video.id;
      this.currentSelectedDeviceInfo.video.id = -1;

      if (!this.isIos) {
        LogService.writeStopTrackLog(this.currentSelectedDeviceInfo.video.id, null, 'video');
        this.wrtcProviderService.detachAndStopVideoTrack(currentDeviceId, false);
      }
    }

    if (this.publishedAudioDeviceId && this.currentSelectedDeviceInfo.audio.id !== -1 && this.publishedAudioDeviceId === this.currentSelectedDeviceInfo.audio.id) {

      if (!this.isIos) {
        this.wrtcProviderService.stopAudioTrack(this.currentSelectedDeviceInfo.audio.id);
      }

      this.currentSelectedDeviceInfo.audio.id = -1;
    }

    // case when is selected track other than current local track
    if (this.currentSelectedDeviceInfo.audio.id !== -1) {
      localStorage.setItem('audio_device_id', this.currentSelectedDeviceInfo.audio.id);
      this.wrtcProviderService.republishLocalParticipantAudioTrack(this.currentSelectedDeviceInfo.audio.id);
      this.wrtcProviderService.reconnectToRoom().subscribe();
    } else {
      if (this.isIos) {
        this.wrtcProviderService.republishLocalParticipantAudioTrack();
      }
    }
    // case when video is changed.
    if (this.currentSelectedDeviceInfo.video.id !== -1) {
      localStorage.setItem('video_device_id', this.currentSelectedDeviceInfo.video.id);
      LogService.writeLog(`Update video device id to ${this.currentSelectedDeviceInfo.video.id}`, LogLevel.INFO, LogResource.WRTC);
      this.wrtcProviderService.republishLocalParticipantVideoTrack(this.currentSelectedDeviceInfo.video.id);
    }
    // case when video doesn't change.
    else {
      if (this.isIos) {
        this.wrtcProviderService.republishLocalParticipantVideoTrack();
      } else {
        this.wrtcProviderService.reattachLocalParticipantVideoTrack();
      }
    }
    this.dialog.dismiss();
  }

  onSelectCameraDevice(event, deviceIndex) {
    if (event) {
      event.preventDefault();
    }

    const device = this.videoDevicesMetadata[deviceIndex];
    if (device.active) {
      const currentDeviceId = this.videoDevicesMetadata[deviceIndex].id;

      this.wrtcProviderService.loadAndAttachVideoTrackById(currentDeviceId, this.videoMediaWrapper.nativeElement).subscribe((trackId) => {
        if (this.currentSelectedDeviceInfo.video.id !== -1) {
          this.wrtcProviderService.detachAndStopVideoTrack(this.currentSelectedDeviceInfo.video.id, true);
          if (this.currentSelectedDeviceInfo.video.id === this.publishedVideoDeviceId && !this.isIos) {
            this.wrtcProviderService.reattachLocalParticipantVideoTrack();
          }
        }
        this.currentSelectedDeviceInfo.video.id = currentDeviceId;

      }, error => {
        LogService.writeLog(`Can not get video track from device ${device.deviceId}`, LogLevel.WARN, LogResource.WRTC);
        this.videoDevicesMetadata[deviceIndex].active = false;
        this.cameraDevice = this.currentSelectedDeviceInfo.video.id;
      });
    }

  }

  onSelectMicrophoneDevice(deviceIndex) {
    const device = this.audioDevicesMetadata[deviceIndex];
    if (device.active) {
      this.currentSelectedDeviceInfo.audio.id = device.id;
      this.wrtcProviderService.updateLocalDevice(device.id);
      clearInterval(this.voiceMeterInterval);

      this.wrtcProviderService.getAudioTrackByDeviceId(device.id).then((track: LocalAudioTrack): void => {
        const audioContext: AudioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser: AnalyserNode = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.5;

        audioContext.resume().then(() => {
          const stream: MediaStream = new MediaStream([track.mediaStreamTrack]);
          const source: MediaStreamAudioSourceNode = audioContext.createMediaStreamSource(stream);
          source.connect(analyser);

          const emitter: EventEmitter<number> = new EventEmitter<number>();

          this.voiceMeterInterval = setInterval(() => {
            this.wrtcProviderService.emmitVoiceLevel(source, analyser, stream, emitter);
          }, 100);

          emitter.subscribe(value => {
            this.currentSelectedDeviceInfo.audio.level = value;
          });
        });
      });
    }
  }
}
